<template>
  <v-card flat color="transparent" class="bookmark-item--card d-flex flex-column">
    <BulkSelectCheckbox
      v-if="isBulkActionsActive"
      class="checkbox ml-1 mt-1"
      :checked="isSelected"
      @click="toggleSelect"
    />
    <div
      class="d-flex flex-row mb-2 tile-link cursor-pointer"
      :title="bookmark.title"
      @click="onContentClick"
    >
      <v-col cols="4" class="bookmark-thumbnail-container pa-0">
        <BookmarkThumbnail :bookmark="bookmark" />
        <BrandButton
          v-if="!isBulkActionsActive"
          class="outgoing-link-btn"
          size="small"
          icon
          flat
          :title="t(bookmark.isImage ? 'cta.download_original' : 'cta.view_original_content')"
          @click.stop.prevent="openSource"
        >
          <PhArrowSquareOut class="text-h6 text-neutral900" />
        </BrandButton>
      </v-col>
      <v-col cols="8" class="pa-0">
        <div
          class="pr-0 pl-2 py-0 bookmark-headline text-body-1 font-weight-semibold text-neutral900"
        >
          <v-progress-circular
            v-if="bookmark.isLoading && !bookmark.hasSufficientData"
            indeterminate size="16" color="blue400" width="3"
            class="mr-1"
          />
          {{ bookmark.title ?? bookmark.domain }}
        </div>
      </v-col>
    </div>

    <div class="text-neutral600 d-flex align-center mb-3 text-caption">
      <BookmarkSource :bookmark="bookmark" />
      <span class="mx-1">&middot;</span>
      <BookmarkDate :value="bookmark.createdAt" />
    </div>

    <BookmarkTags v-if="bookmark.tags.length" class="mb-3" :tags="bookmark.tags" @tag-selected="onTagSelected" />

    <v-spacer />

    <BookmarkActions
      :key="bookmark.id" :bookmark="bookmark"
      :user-id="userId"
      :public-mode="publicMode"
      :membership="membership"
      class="text-text"
      @favorite="$emit('favorite')"
      @save="$emit('save')"
      @add-to-collection="$emit('add-to-collection')"
      @add-to-collection-from-collection="$emit('add-to-collection-from-collection')"
      @comments="$emit('comments')"
      @like="$emit('like')"
      @likes="$emit('likes')"
      @pin-edit="$emit('pin-edit')"
    />
  </v-card>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { PhArrowSquareOut } from '@phosphor-icons/vue'
import BookmarkSource from './BookmarkSource'
import BookmarkActions from './BookmarkActions'
import BookmarkDate from './BookmarkDate'
import BookmarkTags from './BookmarkTags'
import BookmarkThumbnail from './BookmarkThumbnail'
import BrandButton from '@/components/global/brand/BrandButton.vue'
import { useBulkActionsV2 } from '@/composables/useBulkActionsV2'
import { useBookmarkItemSourceUrl } from '@/composables/useBookmarkSourceUrl'
import BulkSelectCheckbox from '@/components/BulkSelectCheckbox.vue'


const props = defineProps({
  bookmark: {
    type: Object,
    required: true,
  },
  userId: {
    type: String,
    default: null,
  },
  membership: {
    type: Object,
    default: null,
  },
  publicMode: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'toggle-select',
  'tag-selected',
  'favorite',
  'save',
  'add-to-collection',
  'add-to-collection-from-collection',
  'comments',
  'like',
  'likes',
  'pin-edit',
])

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { isBulkActionsActive, isItemSelected } = useBulkActionsV2()

const isSelected = computed(() => isItemSelected(props.bookmark.id))

const detailRoute = computed(() => {
  if (props.publicMode) {
    const username = route.params.username
    const cid = route.params.id || props.bookmark.ownerId
    return `/u/${username}/collections/${cid}/bookmarks/${props.bookmark.id}`
  }
  if (props.bookmark.isCollectionBookmark) {
    return `/collections/${props.bookmark.ownerId}/bookmarks/${props.bookmark.id}`
  }
  return `/bookmarks/${props.bookmark.id}`
})

function onContentClick() {
  if (isBulkActionsActive.value) {
    toggleSelect()
  } else {
    router.push(detailRoute.value)
  }
}

const { openSource } = useBookmarkItemSourceUrl(props)

function toggleSelect() {
  emit('toggle-select', props.bookmark)
}

function onTagSelected(tag) {
  emit('tag-selected', tag)
}
</script>

<style lang="scss" scoped>
.bookmark-item--card {
  height: 100%;

  .checkbox {
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1;
  }
}

.tile-link {
  text-decoration: none;
  color: inherit;
}

.bookmark-thumbnail-container {
  position: relative;

  .outgoing-link-btn {
    position: absolute;
    top: 4px;
    right: 4px;
    background-color: rgb(var(--v-theme-neutral0), 0.85) !important;
    z-index: 2;
  }
}

.bookmark-headline {
  -webkit-box-orient: vertical !important;
  -webkit-line-clamp: 2 !important;
  line-clamp: 2 !important;
  display: -webkit-box !important;
  overflow: hidden !important;
  word-break: break-word !important;
  line-height: 1.4 !important;
}
</style>
